type HapticImpact = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotify = 'error' | 'success' | 'warning';

type TgUser = {
  id?: number;
  first_name?: string;
  username?: string;
  language_code?: string;
};

type TgWebApp = {
  ready?: () => void;
  expand?: () => void;
  /** Bot API 7.7+: disables iOS pull-down-to-close gesture so page can scroll vertically. */
  disableVerticalSwipes?: () => void;
  /** Bot API 7.7+: disables iOS swipe-left/right-to-close gesture so our section swipe takes priority. */
  disableHorizontalSwipes?: () => void;
  /** Bot API 6.2+: confirm dialog on accidental close. */
  enableClosingConfirmation?: () => void;
  version?: string;
  initData?: string;
  initDataUnsafe?: { user?: TgUser; start_param?: string };
  HapticFeedback?: {
    impactOccurred?: (style: HapticImpact) => void;
    notificationOccurred?: (kind: HapticNotify) => void;
    selectionChanged?: () => void;
  };
  themeParams?: Record<string, string | undefined>;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TgWebApp;
    };
  }
}

function getTg(): TgWebApp | null {
  if (typeof window === 'undefined') return null;
  const w = window.Telegram?.WebApp;
  return w ?? null;
}

/** True when running inside Telegram Mini App. */
export function isInTelegram(): boolean {
  return getTg() !== null;
}

/** Call once at startup. Tells Telegram the app is ready, requests fullscreen, and sets dark theme colors. */
export function tgReady(): void {
  const tg = getTg();
  if (!tg) return;
  try {
    tg.ready?.();
    tg.expand?.();
    // Без этого iOS TG жрёт vertical swipe жестом pull-down-to-close, ломая scroll
    // внутри Hideout/Codex/Stats секций (Bot API 7.7+, optional chaining = no-op на старых клиентах).
    tg.disableVerticalSwipes?.();
    // Prevent TG's own swipe-to-close from competing with our horizontal section swipe (Bot API 7.7+).
    tg.disableHorizontalSwipes?.();
    tg.enableClosingConfirmation?.();
    // Match our cyber-dark theme
    tg.setHeaderColor?.('#000010');
    tg.setBackgroundColor?.('#000010');
  } catch {
    /* defensive — never throw */
  }
}

/** TG user info if available — used for personalization. */
export function getTgUser(): { firstName: string; languageCode: string } | null {
  const tg = getTg();
  const u = tg?.initDataUnsafe?.user;
  if (!u) return null;
  return {
    firstName: u.first_name ?? 'Commander',
    languageCode: (u.language_code ?? 'en').toLowerCase(),
  };
}

import { getUserSettings } from './userSettings';

export const haptic = {
  impact(level: HapticImpact = 'light'): void {
    if (!getUserSettings().hapticEnabled) return;
    try {
      getTg()?.HapticFeedback?.impactOccurred?.(level);
    } catch { /* ignore */ }
  },
  notify(kind: HapticNotify): void {
    if (!getUserSettings().hapticEnabled) return;
    try {
      getTg()?.HapticFeedback?.notificationOccurred?.(kind);
    } catch { /* ignore */ }
  },
  tick(): void {
    if (!getUserSettings().hapticEnabled) return;
    try {
      getTg()?.HapticFeedback?.selectionChanged?.();
    } catch { /* ignore */ }
  },
};
